import { GraphResolverBase } from './GraphResolverBase';
import { ComponentFactory, ViewContainerRef } from '@angular/core';
import { ConfigDataService } from '../services/config-data.service';
import { GraphDataService } from '../services/graph-data-service.service';
import { ViewPeriodService } from '../services/view-period.service';
import { BehaviorSubject, Subscription, combineLatest, map, tap } from 'rxjs';
import { PopoverController } from '@ionic/angular';
import { GenericBarChartData } from '../objects/chart';
import { CHART_PRIMARY_COLOR, CHART_SECONDARY_COLOR } from '../configs/color';
import { DynamicBarChartWrapperComponent } from '../shared-components/dynamic/dynamic-bar-chart-wrapper/dynamic-bar-chart-wrapper.component';
import { showGateCustomTooltips } from '../helpers/gateTooltips';
import { isScreenSmallerThanMD, isScreenSmallerThanXL } from '../helpers/util';
import { DynamicGraphAdditionalInput, GraphDataConfig, GraphInfoPopover, GraphInfoPopoverByPeriod } from '../objects/config';
import { getAdditionalInput } from '../helpers/DynamicGraphHelper';
import { GraphDependency } from '../enum/graph-dependency.enum';
import { CustomTextTooltipsComponent } from '../pages/home/general/custom-text-tooltips/custom-text-tooltips.component';
import { generateNestedData } from '../helpers/mock-data-generator';
import { EntranceExitData } from '../objects/fetchObejct';

type PinDirection = 'auto' | 'entrance' | 'exit';
type FilterType = 'TOP_5' | 'ALL';
type TwoDepthEntranceExit = Record<string, Record<string, Record<string, EntranceExitData>>>;   // building -> floor -> pin -> data
type OneDepthEntranceExit = Record<string, Record<string, EntranceExitData>>;                   // building -> pin -> data
type FlattenPinEntranceExit = { pin: string; entrance: number; exit: number };
type HexColor = `${string}`;
interface ExtractOptions {
    /** only process these top-level keys (buildings or zones) */
    includeKey1?: string[];
    /** skip these top-level keys entirely */
    excludeKey1?: string[];
    /** only process these second-level keys (floors or direct pin maps) */
    includeKey2?: string[];
    /** skip these second-level keys entirely */
    excludeKey2?: string[];
}

export class EntranceExitAnalysisResolver extends GraphResolverBase {

    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        popoverController: PopoverController,
        viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {
        const useVehicleParkingData = (getAdditionalInput(additionalInput, 'useVehicleParkingData')) as boolean;
        const isMockData = (getAdditionalInput(additionalInput, 'isMockData')) as boolean;
        const gateList = (getAdditionalInput(additionalInput, 'gateList') ?? []) as string[];
        // load data here
        if (useVehicleParkingData) {
            graphDataService.baseGraphData.addDependency(GraphDependency.VEHICLE_PARKING_ENTRANCE_EXIT_ALL_PIN);
        } else if (!isMockData) {
            graphDataService.baseGraphData.addDependency(this.dataDependency);
        }

        const additionnalAspectRatioInput = (getAdditionalInput(additionalInput, 'aspectRatio') || { desktop: 1.0, mobile: 0.7 }) as { desktop: number; mobile: number };
        const chartTitleKey = (getAdditionalInput(additionalInput, 'chartTitleKey') as string);
        const buildingNameInput = (getAdditionalInput(additionalInput, 'building')) as string;
        const floorNameInput = (getAdditionalInput(additionalInput, 'floor') || configDataService.GRAPH_CONFIG.ENTRANCE_EXIT_ANALYSIS.FLOOR) as string;
        const sortBy = (getAdditionalInput(additionalInput, 'sortBy') || configDataService.GRAPH_CONFIG.ENTRANCE_EXIT_ANALYSIS.SORT_BY || 'auto') as PinDirection;
        const filterBy = (getAdditionalInput(additionalInput, 'filterBy') || configDataService.GRAPH_CONFIG.ENTRANCE_EXIT_ANALYSIS.FILTER_BY) as FilterType;
        const contributeLevel = (getAdditionalInput(additionalInput, 'contributeLevel') || configDataService.GRAPH_CONFIG.ENTRANCE_EXIT_ANALYSIS.CONTRIBUTE_LEVEL) as string;
        const channel = (getAdditionalInput(additionalInput, 'channel') || configDataService.GRAPH_CONFIG.ENTRANCE_EXIT_ANALYSIS.CHANNEL || 'auto') as PinDirection;
        const dynamicGraphDataSelector = (getAdditionalInput(additionalInput, 'dynamicGraphDataSelector')) as { [selectorName: string]: GraphDataConfig };
        const showPercentOfTotal = (getAdditionalInput(additionalInput, 'showPercentOfTotal') || {}) as boolean;
        const getFloorGateData = (value: { [buildingName: string]: { [floorName: string]: { [gateName: string]: number } } }, defaultValue = {}) => (value && value[buildingNameInput]) ? value[buildingNameInput] : defaultValue as { [floorName: string]: { [gateName: string]: number } };
        const showAveragePerDay = (getAdditionalInput(additionalInput, 'showAveragePerDay') || false) as boolean;
        const displayOnPeriodType = (getAdditionalInput(additionalInput, 'displayOnPeriodType')) as { live: boolean; day: boolean; week: boolean; month: boolean };
        const getColor = (lineName: string) => ((((configDataService.GRAPH_CONFIG.INTERACTABLE_BAR_CHART || {})[chartTitleKey] || {}).barConfig || {})[lineName] || {}) as { color: string; backgroundColor?: string };
        const graphInfoPopoverByPeriod = (getAdditionalInput(additionalInput, 'graphInfoPopoverByPeriod')) as GraphInfoPopoverByPeriod;
        const updateTextByPeriod = (getAdditionalInput(additionalInput, 'updateTextByPeriod') || false) as boolean;
        const isMock = configDataService.isFeatureEnabled('mock_data', 'building_entrance_exit_by_pin');
        const selectedDataSource$ = new BehaviorSubject<{ [selectorName: string]: GraphDataConfig }>(dynamicGraphDataSelector);
        const floorInitialEntranceData = contributeLevel === 'building' ? graphDataService.allPinByBuildingEntrance$.getValue() || {} : contributeLevel === 'zone' ? graphDataService.allPinByZoneEntrance$.value || {} : getFloorGateData(graphDataService.floorGateEntranceData$.value)[floorNameInput] || {};
        const floorInitialExitData = contributeLevel === 'building' ? graphDataService.allPinByBuildingExit$.getValue() || {} : contributeLevel === 'zone' ? graphDataService.allPinByZoneExit$.value || {} : getFloorGateData(graphDataService.floorGateExitData$.value)[floorNameInput] || {};
        const sortedGateInitialData = sortBy === 'entrance' ? Object.fromEntries(Object.entries(floorInitialEntranceData).sort((a, b) => b[1] - a[1])) : Object.fromEntries(Object.entries(floorInitialExitData).sort((a, b) => b[1] - a[1]));
        const filteredGateInitialData = filterBy === 'TOP_5' ? Object.fromEntries(Object.entries(sortedGateInitialData).slice(0, 5)) : sortedGateInitialData;
        const initialGateNames = sortBy === 'auto' ? Object.keys(floorInitialEntranceData).sort() : Object.keys(filteredGateInitialData);
        const chartLabel = initialGateNames.map(gateName => configDataService.DISPLAY_LANGUAGE.GATE_NAME[gateName] || gateName);

        const buildingChannelTotal: { entrance: number; exit: number } = { entrance: 0, exit: 0 };
        const entranceInitialData = initialGateNames.map(gateName => sortBy === 'entrance' ? filteredGateInitialData[gateName] || null : floorInitialEntranceData[gateName] || null);
        const exitInitialData = initialGateNames.map(gateName => sortBy === 'exit' ? filteredGateInitialData[gateName] || null : floorInitialExitData[gateName] || null);
        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicBarChartWrapperComponent;
        const dualBarData$ = new BehaviorSubject<GenericBarChartData[]>([]);
        const pinsData$ = combineLatest([
            selectedDataSource$,
            graphDataService.buildingEntranceExitByPinData$,
            graphDataService.floorEntranceExitPin$,
            graphDataService.entranceExitZoneByPinData$,
            graphDataService.buildingEntranceExitByPinAvgPerDayData$
        ]);
        const chartColor: { entrance: HexColor, exit: HexColor } = { entrance: CHART_PRIMARY_COLOR, exit: CHART_SECONDARY_COLOR };
        if (isMockData) {
            let mockData = await generateNestedData(viewPeriodService.selectedDate, viewPeriodService, configDataService, 'ENTRANCE_EXIT_ANALYSIS', 'count', 5);
            const mockGateArray = gateList;
            dualBarData$.next([{
                data: mockData.entrance,
                color: CHART_PRIMARY_COLOR,
                label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
                calPercentFrommAllData: false
            }, {
                data: mockData.exit,
                color: CHART_SECONDARY_COLOR,
                label: configDataService.DISPLAY_LANGUAGE.EXIT,
                calPercentFrommAllData: false
            }]);
            subscription.add(combineLatest([viewPeriodService.dayList])
                .subscribe(async ([dayList]) => {
                    mockData = await generateNestedData(viewPeriodService.selectedDate, viewPeriodService, configDataService, 'ENTRANCE_EXIT_ANALYSIS', 'count', 5);

                    if (displayOnPeriodType) {
                        comInstance.isShow = viewPeriodService.isLiveMode ? displayOnPeriodType.live : displayOnPeriodType[viewPeriodService.viewPeriod.backendName];
                    }
                    comInstance.chartLabel = mockGateArray.map(gateName => configDataService.DISPLAY_LANGUAGE.GATE_NAME[gateName] || gateName);
                    const dualBarData: GenericBarChartData[] = channel === 'auto' ?
                        [{
                            data: mockData.entrance,
                            color: CHART_PRIMARY_COLOR,
                            label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
                            calPercentFrommAllData: true,
                            calPercentFromLabelProperty: true,
                        }, {
                            data: mockData.exit,
                            color: CHART_SECONDARY_COLOR,
                            label: configDataService.DISPLAY_LANGUAGE.EXIT,
                            calPercentFrommAllData: true,
                            calPercentFromLabelProperty: true,
                        }]
                        :
                        [{
                            data: channel === 'entrance' ? mockData.entrance : mockData.exit,
                            color: CHART_PRIMARY_COLOR,
                            label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
                            calPercentFrommAllData: false,
                            barPercentage: 0.7
                        }];
                    comInstance.suggestedTickMax_X = this.updateMaxTickX(sortBy, sortBy === "entrance" ? mockData.entrance[0] : mockData.exit[0]);
                    dualBarData$.next(dualBarData);
                }));
        } else {
            pinsData$.pipe(
                tap(() => {
                    if (displayOnPeriodType) {
                        comInstance.isShow = viewPeriodService.isLiveMode ? displayOnPeriodType.live : displayOnPeriodType[viewPeriodService.viewPeriod.backendName];
                    }
                    if (graphInfoPopoverByPeriod) {
                        const graphInfoPopover = this.selectInfoPopoverByPeriod(graphInfoPopoverByPeriod, viewPeriodService);
                        comInstance.infoPopover = async (e: any) => {
                            const popover = await popoverController.create({
                                component: CustomTextTooltipsComponent,
                                componentProps: {
                                    toolTipTitle: graphInfoPopover.title || configDataService.DISPLAY_LANGUAGE[chartTitleKey],
                                    toolTipDetails: graphInfoPopover.details
                                },
                                cssClass: 'customer-segment-details-popover',
                                event: e,
                            });
                            return popover.present();
                        };
                    }
                }),
                map((pins) => {
                    let pinData: OneDepthEntranceExit | TwoDepthEntranceExit;
                    switch (contributeLevel) {
                        case "floor":
                            pinData = { ...pins[2] }
                            break;
                        case "zone":
                            pinData = { ...pins[3] }
                            break;
                        default:
                            pinData = showAveragePerDay ? { ...pins[4] } : { ...pins[1] };
                            break;
                    }
                    return {
                        selector: pins[0],
                        pinData: pinData
                    }
                }),
                map(inputs => {
                    let extractedPinData: Array<FlattenPinEntranceExit> = [];
                    if (typeof inputs.selector === 'object') {
                        let key1 = Object.values(inputs.selector).filter(config => config.selected).map(config => config.args[0]);
                        let extractOpts: ExtractOptions = { includeKey1: key1, excludeKey1: ["_total"], excludeKey2: ["_total"] };
                        extractedPinData = this.extractPinData(inputs.pinData, extractOpts);
                        let totalEntrance = Object.values(extractedPinData).reduce((acc, val) => acc + val.entrance, 0);
                        let totalExit = Object.values(extractedPinData).reduce((acc, val) => acc + val.exit, 0);
                        buildingChannelTotal.entrance = totalEntrance;
                        buildingChannelTotal.exit = totalExit;
                        chartColor.entrance = getColor(key1[0]).color;
                        chartColor.exit = getColor(key1[0]).color;
                    } else {
                        let extractOpts: ExtractOptions = { excludeKey1: ["_total"], excludeKey2: ["_total"] };
                        extractedPinData = this.extractPinData(inputs.pinData, extractOpts);
                    }
                    const processedPinData = this.processPinData(extractedPinData, sortBy, filterBy);
                    const entranceData = processedPinData.map(data => data.entrance);
                    const exitData = processedPinData.map(data => data.exit);
                    comInstance.chartLabel = processedPinData.map(pinData => configDataService.DISPLAY_LANGUAGE.GATE_NAME[pinData.pin] || pinData.pin);
                    comInstance.suggestedTickMax_X = this.updateMaxTickX(sortBy, channel !== "exit" ? entranceData : exitData);
                    return channel === "auto" ? [
                        {
                            data: entranceData,
                            color: chartColor.entrance,
                            label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
                        },
                        {
                            data: exitData,
                            color: chartColor.exit,
                            label: configDataService.DISPLAY_LANGUAGE.EXIT,
                        }
                    ] :
                        [{
                            data: channel === 'entrance' ? entranceData : exitData,
                            color: channel === 'entrance' ? chartColor.entrance : chartColor.exit,
                            label: configDataService.DISPLAY_LANGUAGE.ENTRANCE,
                            calPercentFrommAllData: false,
                            barPercentage: 0.7
                        }];
                })
            ).subscribe(dualBarData$);
        }
        comInstance.suggestedTickMax_X = this.updateMaxTickX(sortBy, channel !== "exit" ? entranceInitialData : exitInitialData);
        comInstance.title = configDataService.DISPLAY_LANGUAGE[chartTitleKey] || configDataService.DISPLAY_LANGUAGE.ENTRANCE_EXIT_ANALYSIS;
        comInstance.isLock = this.isLock;
        comInstance.isShowLegend = channel === 'auto';
        comInstance.data$ = dualBarData$;
        comInstance.chartLabel = chartLabel;
        comInstance.isHorizontal = true;
        comInstance.aspectRatio = isScreenSmallerThanXL(window) ? additionnalAspectRatioInput.mobile : additionnalAspectRatioInput.desktop;
        comInstance.valueTextAlign = 'left';
        comInstance.valueTextOffsetX = isScreenSmallerThanMD(window) ? 1 : 5;
        comInstance.valueTextOffsetY = 5;
        comInstance.valueTextProcess = 'numberFormatter';
        comInstance.displayAxis = 'Y';
        comInstance.isStacked = '!XY';
        comInstance.displayGrid = channel === 'auto';
        comInstance.selectedBar$ = selectedDataSource$;
        comInstance.selectInterface = 'alert';
        comInstance.selector = dynamicGraphDataSelector;
        comInstance.multipleSelector = false;
        // comInstance.showAveragePerDay = showAveragePerDay;
        const imageSrc = configDataService.isFeatureEnabled('traffic_page', 'gate_image_flatten') ? configDataService.entranceExitPinImageSrc : configDataService.entranceExitGateImageSrc;
        comInstance.customToolTipFuction = function(tooltipModel) {
            (tooltipModel as any).floorName = floorNameInput;
            const channelName: 'entrance' | 'exit' | 'auto' = channel === 'auto' ? 'entrance' : channel;
            if (typeof dynamicGraphDataSelector === "object") {
                showGateCustomTooltips(tooltipModel, this, imageSrc, configDataService.DISPLAY_LANGUAGE.GATE_NAME, channelName, showPercentOfTotal, buildingChannelTotal);
            } else {
                showGateCustomTooltips(tooltipModel, this, imageSrc, configDataService.DISPLAY_LANGUAGE.GATE_NAME, channelName);
            }
        };
        if (graphInfoPopoverByPeriod) {
            const infoPopoverText = graphInfoPopoverByPeriod;
            let infoPopoverPeriod = infoPopoverText.default;
            if (updateTextByPeriod) {
                if (viewPeriodService.isLiveMode) {
                    infoPopoverPeriod = infoPopoverText.default;
                } else if (viewPeriodService.isDayPeriod) {
                    infoPopoverPeriod = infoPopoverText.day;
                } else if (viewPeriodService.isWeekPeriod) {
                    infoPopoverPeriod = infoPopoverText.week;
                } else if (viewPeriodService.isMonthPeriod) {
                    infoPopoverPeriod = infoPopoverText.month;
                }
            }
            comInstance.infoPopover = async (e: any) => {
                const popover = await popoverController.create({
                    component: CustomTextTooltipsComponent,
                    componentProps: {
                        toolTipTitle: infoPopoverPeriod.title || configDataService.DISPLAY_LANGUAGE[chartTitleKey],
                        toolTipDetails: infoPopoverPeriod.details
                    },
                    cssClass: 'customer-segment-details-popover',
                    event: e,
                });
                return await popover.present();
            };
        }
        return comInstance;
    }

    private calculateFragmentValue(val: number): number {
        return val > 10000 ? Math.round(val / 5) : Math.round(val / 10);
    }

    private updateMaxTickX(direction: PinDirection, data: number[]): number {
        if (data.length === 0 || direction === "auto") {
            return 0;
        }
        return data[0] + this.calculateFragmentValue(data[0]);
    }

    extractPinData(
        data: OneDepthEntranceExit | TwoDepthEntranceExit,
        opts: ExtractOptions = {}
    ): Array<{ pin: string; entrance: number; exit: number }> {
        const out: Array<{ pin: string; entrance: number; exit: number }> = [];
        const seenPins = new Set<string>();

        for (const key1 in data) {
            if (opts.includeKey1 && !opts.includeKey1.includes(key1)) continue;
            if (opts.excludeKey1 && opts.excludeKey1.includes(key1)) continue;

            const level2 = (data as any)[key1];
            for (const key2 in level2) {
                if (opts.includeKey2 && !opts.includeKey2.includes(key2)) continue;
                if (opts.excludeKey2 && opts.excludeKey2.includes(key2)) continue;

                const maybeRecOrGroup = level2[key2];

                if (
                    maybeRecOrGroup &&
                    typeof maybeRecOrGroup === 'object' &&
                    'entrance' in maybeRecOrGroup &&
                    'exit' in maybeRecOrGroup
                ) {
                    const rec = maybeRecOrGroup as EntranceExitData;
                    const pin = key2;
                    if (
                        typeof rec.entrance === 'number' &&
                        typeof rec.exit === 'number' &&
                        !seenPins.has(pin)
                    ) {
                        out.push({ pin, entrance: rec.entrance, exit: rec.exit });
                        seenPins.add(pin);
                    }

                } else {
                    const pinGroup = maybeRecOrGroup as Record<string, EntranceExitData>;
                    for (const pinName in pinGroup) {
                        const rec = pinGroup[pinName];
                        if (
                            rec &&
                            typeof rec.entrance === 'number' &&
                            typeof rec.exit === 'number' &&
                            !seenPins.has(pinName)
                        ) {
                            out.push({ pin: pinName, entrance: rec.entrance, exit: rec.exit });
                            seenPins.add(pinName);
                        }
                    }
                }
            }
        }

        return out;
    }

    private processPinData(
        pinData: Array<FlattenPinEntranceExit>,
        sortBy: PinDirection,
        filterBy: FilterType,
    ): Array<FlattenPinEntranceExit> {
        if (sortBy === 'auto') {
            return pinData;
        }
        const sorted = sortBy === "entrance" ? pinData.sort((a, b) => b.entrance - a.entrance) : pinData.sort((a, b) => b.exit - a.exit);
        const sliced = filterBy === 'TOP_5'
            ? sorted.slice(0, 5)
            : sorted;
        return sliced;
    }

    private selectInfoPopoverByPeriod(graphInfoPopoverByPeriod: GraphInfoPopoverByPeriod, viewPeriodService: ViewPeriodService): GraphInfoPopover {
        if (!graphInfoPopoverByPeriod) return;
        let graphInfoPopover: GraphInfoPopover;
        if (viewPeriodService.isDayPeriod) {
            graphInfoPopover = graphInfoPopoverByPeriod.day;
        } else if (viewPeriodService.isWeekPeriod) {
            graphInfoPopover = graphInfoPopoverByPeriod.week;
        } else if (viewPeriodService.isMonthPeriod) {
            graphInfoPopover = graphInfoPopoverByPeriod.month;
        } else {
            graphInfoPopover = graphInfoPopoverByPeriod.default;
        }
        return graphInfoPopover
    }

}

